/**
 * components/Logo
 * The SK suite mark: a charcoal rounded tile carrying the SK monogram, matching
 * SK Salon (SK-SALOON/web/index.html) and web/favicon.svg so the header, the
 * browser tab and the rest of the suite all show the same thing.
 *
 * It replaced a Company-Cash-only banknote glyph. A per-product mark makes each
 * console look like a separate company in a tab bar that usually has four of
 * them open at once; the suite reads as one product with one mark.
 *
 * THE MONOGRAM IS A VECTOR PATH, NOT TEXT. Salon draws it with <text> in
 * Lexend/Arial, which resolves to a different face on every machine — and on
 * native there is no webfont at all, so the header mark and the favicon would
 * not even match each other. These are the Arial Bold outlines at Salon's own
 * metrics (30px, tracking -1, baseline y=43, centred on x=32), which is what
 * Salon actually renders as wherever Lexend is not installed.
 *
 * Drawn inline — the design ships no image assets, and an inline mark cannot
 * 404 or flash in at a different moment from the wordmark beside it.
 */
import { memo } from 'react';
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

export interface LogoProps {
  size?: number;
}

/** Salon's 64×64 grid, so the two marks are identical rather than similar. */
const SK_MONOGRAM =
  'M12.75 36.01 16.97 35.6Q17.35 37.73 18.51 38.72Q19.68 39.72 21.65 39.72Q23.75 39.72 24.81 38.83Q25.87 37.95 25.87 36.76Q25.87 36 25.42 35.46Q24.98 34.93 23.86 34.53Q23.1 34.27 20.39 33.6Q16.91 32.73 15.5 31.47Q13.52 29.7 13.52 27.15Q13.52 25.51 14.45 24.08Q15.38 22.65 17.13 21.91Q18.88 21.16 21.36 21.16Q25.4 21.16 27.45 22.93Q29.49 24.7 29.59 27.66L25.26 27.85Q24.98 26.2 24.06 25.47Q23.15 24.75 21.32 24.75Q19.43 24.75 18.36 25.52Q17.67 26.02 17.67 26.86Q17.67 27.62 18.31 28.16Q19.13 28.85 22.3 29.6Q25.46 30.34 26.98 31.14Q28.49 31.94 29.35 33.32Q30.21 34.71 30.21 36.75Q30.21 38.59 29.18 40.2Q28.16 41.81 26.28 42.6Q24.41 43.38 21.61 43.38Q17.54 43.38 15.35 41.5Q13.17 39.62 12.75 36.01Z M32.91 43V21.53H37.25V31.06L46.01 21.53H51.84L43.75 29.89L52.28 43H46.67L40.77 32.92L37.25 36.51V43Z';

function LogoBase({ size = 20 }: LogoProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 64 64">
      <Defs>
        <LinearGradient id="skLogoTile" x1="0" y1="0" x2="1" y2="1">
          <Stop offset="0" stopColor="#2A2D34" />
          <Stop offset="1" stopColor="#121316" />
        </LinearGradient>
      </Defs>
      <Rect width={64} height={64} rx={16} fill="url(#skLogoTile)" />
      <Path d={SK_MONOGRAM} fill="#FFFFFF" />
    </Svg>
  );
}

export const Logo = memo(LogoBase);
export default Logo;
